import { useEffect, useMemo, useRef, useState } from 'react'
import { api } from '../lib/api.js'
import { Chip, Empty, ErrorNote } from '../components/ui.jsx'

// Tasks. A task is the definition; a repeating task is a rule, and each
// tickable slot is an occurrence with its own content and attachments,
// because "post 3 times a week" is three separate things to write. A slot left
// unfinished when its period ends reads as missed -- derived, never stored.

const STATUS = [
  ['todo', 'To do', 'not_started'],
  ['doing', 'In progress', 'queued'],
  ['done', 'Done', 'named'],
  ['blocked', 'Blocked', 'stale'],
]
const KIND = Object.fromEntries(STATUS.map(([k, , kind]) => [k, kind]))
const LABEL = Object.fromEntries(STATUS.map(([k, l]) => [k, l]))
KIND.missed = 'stale'
LABEL.missed = 'Missed'

const nameOf = (people, email) => people.find((p) => p.email === email)?.name || email || 'Unassigned'
const bytes = (n) => (n < 1024 ? `${n} B` : n < 1048576 ? `${(n / 1024).toFixed(0)} KB` : `${(n / 1048576).toFixed(1)} MB`)

// A period label reads as a date range, because "2026-W37" means nothing at a glance.
function periodLabel(period, end) {
  if (!period) return ''
  if (period.includes('-W') || period.includes('-F')) {
    const d = new Date(`${end}T00:00:00Z`)
    const start = new Date(d)
    start.setUTCDate(start.getUTCDate() - (period.includes('-F') ? 13 : 6))
    const f = (x) => x.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', timeZone: 'UTC' })
    return `${f(start)} – ${f(d)}`
  }
  return new Date(`${period}-01T00:00:00Z`).toLocaleDateString('en-GB', { month: 'long', year: 'numeric', timeZone: 'UTC' })
}

function Attachments({ taskId, occurrenceId, items, onChanged }) {
  const input = useRef(null)
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState(null)

  const upload = async (e) => {
    const file = e.target.files?.[0]
    if (!file) return
    setBusy(true); setErr(null)
    try {
      await api.uploadAttachment(taskId, file, { occurrence: occurrenceId })
      onChanged()
    } catch (x) { setErr(x) } finally { setBusy(false); if (input.current) input.current.value = '' }
  }

  return (
    <div style={{ marginTop: 6 }}>
      <div className="row" style={{ gap: 6, flexWrap: 'wrap' }}>
        {(items || []).map((a) => (
          <span key={a.id} className="row" style={{ gap: 4, background: 'var(--line-soft)', padding: '2px 6px' }}>
            {/* Fetched with the session's token rather than linked directly:
                a bare link carries no bearer token, so it would 401 in a
                signed-in deployment. */}
            <button className="btn link" style={{ fontSize: 'var(--fs-pill)' }} title="Open"
              onClick={() => api.openAttachment(occurrenceId || taskId, a.stored, a.name).catch(setErr)}>
              {a.name}
            </button>
            <span className="mono" style={{ fontSize: '10px', color: 'var(--tertiary)' }}>{bytes(a.size)}</span>
            <button className="btn link" style={{ fontSize: '10px' }} title="Remove"
              onClick={async () => { await api.deleteAttachment(taskId, a.id, occurrenceId); onChanged() }}>×</button>
          </span>
        ))}
        <button className="btn" disabled={busy} onClick={() => input.current?.click()}>
          {busy ? 'Uploading…' : '+ Attach'}
        </button>
        <input ref={input} type="file" style={{ display: 'none' }} onChange={upload} />
      </div>
      {err ? <div style={{ color: 'var(--neg)', fontSize: 'var(--fs-pill)', marginTop: 3 }}>{err.message}</div> : null}
    </div>
  )
}

function TaskDetail({ task, people, accounts, campaigns, onClose, onChanged }) {
  const [form, setForm] = useState(task)
  const [showAll, setShowAll] = useState(false)
  useEffect(() => setForm(task), [task])

  const save = async (fields) => {
    setForm((f) => ({ ...f, ...fields }))
    await api.patchTask(task.id, fields)
    onChanged()
  }
  const saveSlot = async (occId, fields) => { await api.patchOccurrence(occId, fields); onChanged() }

  return (
    <>
      <div className="scrim" onClick={onClose} />
      <div className="slideover" role="dialog" aria-label={task.title}>
        <div className="slide-head">
          <h1>{form.title}</h1>
          <button className="slide-close" onClick={onClose} aria-label="Close">×</button>
        </div>

        <div className="slide-body">
          <div className="col">
          <div className="card" style={{ marginBottom: 12 }}>
            <div className="card-header"><span className="card-title">Task</span></div>
            <div className="stack" style={{ gap: 8, padding: '10px 12px' }}>
              <label className="stack" style={{ gap: 3 }}>
                <span className="lbl">Title</span>
                <input className="input" value={form.title || ''}
                  onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))}
                  onBlur={(e) => save({ title: e.target.value })} />
              </label>

              <div className="row" style={{ gap: 8 }}>
                <label className="stack grow" style={{ gap: 3 }}>
                  <span className="lbl">Assignee</span>
                  <select className="input" value={form.assignee || ''} onChange={(e) => save({ assignee: e.target.value || null })}>
                    <option value="">Unassigned</option>
                    {people.map((p) => <option key={p.email} value={p.email}>{p.name}</option>)}
                  </select>
                </label>
                <label className="stack" style={{ gap: 3, width: 140 }}>
                  <span className="lbl">Status</span>
                  <select className="input" value={form.status} onChange={(e) => save({ status: e.target.value })}>
                    {STATUS.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
                  </select>
                </label>
              </div>

              <div className="row" style={{ gap: 8 }}>
                <label className="stack grow" style={{ gap: 3 }}>
                  <span className="lbl">Start</span>
                  <input className="input" type="date" value={form.start || ''} onChange={(e) => save({ start: e.target.value })} />
                </label>
                <label className="stack grow" style={{ gap: 3 }}>
                  <span className="lbl">Due</span>
                  <input className="input" type="date" value={form.due || ''} onChange={(e) => save({ due: e.target.value || null })} />
                </label>
              </div>

              <label className="stack" style={{ gap: 3 }}>
                <span className="lbl">Linked to</span>
                <select className="input"
                  value={form.link ? `${form.link.type}:${form.link.id}` : ''}
                  onChange={(e) => {
                    const v = e.target.value
                    save({ link: v ? { type: v.split(':')[0], id: v.slice(v.indexOf(':') + 1) } : null })
                  }}>
                  <option value="">Nothing</option>
                  <optgroup label="Campaigns">
                    {campaigns.map((c) => <option key={c.id} value={`campaign:${c.id}`}>{c.name}</option>)}
                  </optgroup>
                  <optgroup label="Accounts">
                    {accounts.map((a) => <option key={a.slug} value={`account:${a.slug}`}>{a.name}</option>)}
                  </optgroup>
                </select>
              </label>

              <label className="stack" style={{ gap: 3 }}>
                <span className="lbl">Notes</span>
                <textarea className="input" rows={3} value={form.notes || ''}
                  onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))}
                  onBlur={(e) => save({ notes: e.target.value })} />
              </label>

              <div>
                <span className="lbl">Attachments</span>
                <Attachments taskId={task.id} items={form.attachments} onChanged={onChanged} />
              </div>
            </div>
          </div>

          {task.repeat ? (
            <div className="card">
              <div className="card-header">
                <span className="card-title">
                  Repeats {task.repeat.count} × per {task.repeat.every}
                </span>
                <button className="btn" style={{ marginLeft: 'auto' }} onClick={() => setShowAll((v) => !v)}>
                  {showAll ? 'Show fewer' : `Show all ${task.periods.length} periods`}
                </button>
                <span className="card-sub">
                  Each slot has its own content. A slot left unfinished when the period ends reads as missed.
                </span>
              </div>
              <div className="stack" style={{ gap: 0 }}>
                {(showAll ? task.periods : task.periods.filter((p) => p.when !== 'upcoming' || task.periods.indexOf(p) < 5)).map((p) => (
                  <div key={p.period} style={{ padding: '9px 12px', borderBottom: '0.5px solid var(--line)' }}>
                    <div className="row" style={{ gap: 8, alignItems: 'baseline', marginBottom: 5 }}>
                      <span style={{ fontWeight: 600 }}>{periodLabel(p.period, p.period_end)}</span>
                      {p.when === 'current' ? <Chip kind="named">THIS WEEK</Chip> : null}
                      <span className="mono" style={{ fontSize: 'var(--fs-pill)', color: 'var(--muted)' }}>
                        {p.done} of {p.total} done{p.missed ? ` · ${p.missed} missed` : ''}
                      </span>
                    </div>
                    <div className="stack" style={{ gap: 6 }}>
                      {p.slots.map((s) => (
                        <div key={s.id} className="row" style={{ gap: 8, alignItems: 'flex-start' }}>
                          <input type="checkbox" style={{ marginTop: 6 }} checked={s.status === 'done'}
                            onChange={(e) => saveSlot(s.id, { status: e.target.checked ? 'done' : 'todo' })} />
                          <div style={{ flex: 1 }}>
                            <div className="row" style={{ gap: 6, marginBottom: 3 }}>
                              <Chip kind={KIND[s.status]}>{LABEL[s.status]}</Chip>
                              <span className="mono" style={{ fontSize: '10px', color: 'var(--tertiary)' }}>
                                slot {s.slot}
                              </span>
                            </div>
                            <textarea className="input" rows={2} placeholder="What went out, or what to write…"
                              defaultValue={s.content || ''}
                              onBlur={(e) => e.target.value !== (s.content || '') && saveSlot(s.id, { content: e.target.value })} />
                            <Attachments taskId={task.id} occurrenceId={s.id} items={s.attachments} onChanged={onChanged} />
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ) : null}

          <div className="row" style={{ marginTop: 12 }}>
            <button className="btn" style={{ color: 'var(--neg)' }}
              onClick={async () => { if (confirm(`Delete "${task.title}"? This cannot be undone.`)) { await api.deleteTask(task.id); onClose(); onChanged() } }}>
              Delete task
            </button>
          </div>
          </div>
        </div>
      </div>
    </>
  )
}

function NewTask({ people, accounts, campaigns, onClose, onCreated }) {
  const [f, setF] = useState({ title: '', assignee: people[0]?.email || '', start: new Date().toISOString().slice(0, 10), due: '', notes: '', repeats: false, every: 'week', count: 1, link: '' })
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState(null)

  const create = async () => {
    setBusy(true); setErr(null)
    try {
      const body = {
        title: f.title, assignee: f.assignee || null, start: f.start, due: f.due || null, notes: f.notes,
        repeat: f.repeats ? { every: f.every, count: Number(f.count) || 1 } : null,
        link: f.link ? { type: f.link.split(':')[0], id: f.link.slice(f.link.indexOf(':') + 1) } : null,
      }
      const t = await api.createTask(body)
      onCreated(t)
    } catch (x) { setErr(x) } finally { setBusy(false) }
  }

  return (
    <>
      <div className="scrim modal-scrim" onClick={onClose} />
      <div className="modal" role="dialog" aria-label="New task">
        <div className="modal-head">
          <h1 style={{ fontSize: 'var(--fs-title)', fontWeight: 500, margin: 0 }}>New task</h1>
          <button className="slide-close" style={{ marginLeft: 'auto' }} onClick={onClose} aria-label="Close">×</button>
        </div>
        <div className="modal-body stack" style={{ gap: 8 }}>
          <label className="stack" style={{ gap: 3 }}>
            <span className="lbl">Title</span>
            <input className="input" autoFocus placeholder="Matt to kick off campaign 1 launch"
              value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} />
          </label>
          <div className="row" style={{ gap: 8 }}>
            <label className="stack grow" style={{ gap: 3 }}>
              <span className="lbl">Assignee</span>
              <select className="input" value={f.assignee} onChange={(e) => setF({ ...f, assignee: e.target.value })}>
                <option value="">Unassigned</option>
                {people.map((p) => <option key={p.email} value={p.email}>{p.name}</option>)}
              </select>
            </label>
            <label className="stack grow" style={{ gap: 3 }}>
              <span className="lbl">Start</span>
              <input className="input" type="date" value={f.start} onChange={(e) => setF({ ...f, start: e.target.value })} />
            </label>
            <label className="stack grow" style={{ gap: 3 }}>
              <span className="lbl">Due</span>
              <input className="input" type="date" value={f.due} onChange={(e) => setF({ ...f, due: e.target.value })} />
            </label>
          </div>

          <label className="row" style={{ gap: 6, cursor: 'pointer' }}>
            <input type="checkbox" checked={f.repeats} onChange={(e) => setF({ ...f, repeats: e.target.checked })} />
            <span>This repeats</span>
          </label>
          {f.repeats ? (
            <div className="row" style={{ gap: 8, alignItems: 'flex-end' }}>
              <label className="stack" style={{ gap: 3, width: 90 }}>
                <span className="lbl">How many</span>
                <input className="input" type="number" min="1" max="20" value={f.count}
                  onChange={(e) => setF({ ...f, count: e.target.value })} />
              </label>
              <label className="stack" style={{ gap: 3, width: 150 }}>
                <span className="lbl">Per</span>
                <select className="input" value={f.every} onChange={(e) => setF({ ...f, every: e.target.value })}>
                  <option value="week">week</option>
                  <option value="fortnight">fortnight</option>
                  <option value="month">month</option>
                </select>
              </label>
              <span className="card-sub" style={{ paddingBottom: 5 }}>
                {f.count} tickable slot{Number(f.count) === 1 ? '' : 's'} every {f.every}, each with its own content.
              </span>
            </div>
          ) : null}

          <label className="stack" style={{ gap: 3 }}>
            <span className="lbl">Linked to</span>
            <select className="input" value={f.link} onChange={(e) => setF({ ...f, link: e.target.value })}>
              <option value="">Nothing</option>
              <optgroup label="Campaigns">
                {campaigns.map((c) => <option key={c.id} value={`campaign:${c.id}`}>{c.name}</option>)}
              </optgroup>
              <optgroup label="Accounts">
                {accounts.map((a) => <option key={a.slug} value={`account:${a.slug}`}>{a.name}</option>)}
              </optgroup>
            </select>
          </label>

          <label className="stack" style={{ gap: 3 }}>
            <span className="lbl">Notes</span>
            <textarea className="input" rows={3} value={f.notes} onChange={(e) => setF({ ...f, notes: e.target.value })} />
          </label>

          <ErrorNote error={err} />
        </div>
        <div className="modal-foot" style={{ justifyContent: 'flex-end' }}>
          <button className="btn" onClick={onClose}>Cancel</button>
          <button className="btn primary" disabled={busy || !f.title.trim()} onClick={create}>
            {busy ? 'Creating…' : 'Create task'}
          </button>
        </div>
      </div>
    </>
  )
}

export default function Tasks({ accounts, campaigns = [], onOpenAccount }) {
  const [data, setData] = useState(null)
  const [err, setErr] = useState(null)
  const [tab, setTab] = useState('week')
  const [who, setWho] = useState(null)
  const [openId, setOpenId] = useState(null)
  const [creating, setCreating] = useState(false)

  const load = async () => {
    try { setData(await api.tasks()) } catch (e) { setErr(e) }
  }
  useEffect(() => { load() }, [])

  const people = data?.people || []
  const open = data?.tasks?.find((t) => t.id === openId) || null

  const week = useMemo(
    () => (data?.week || []).filter((r) => !who || r.assignee === who),
    [data, who],
  )
  const grouped = useMemo(() => {
    const m = new Map()
    for (const r of week) {
      const k = r.assignee || ''
      if (!m.has(k)) m.set(k, [])
      m.get(k).push(r)
    }
    return [...m.entries()]
  }, [week])

  const allTasks = useMemo(
    () => (data?.tasks || []).filter((t) => !who || t.assignee === who),
    [data, who],
  )

  if (err) return <div className="body"><ErrorNote error={err} /></div>
  if (!data) return <div className="body"><div className="empty">Loading tasks…</div></div>

  const overdue = (data.week || []).filter((r) => r.overdue).length

  return (
    <>
      <div className="topbar">
        <h1>Tasks</h1>
        <span className="topbar-note">
          {data.tasks.length} task{data.tasks.length === 1 ? '' : 's'}
          {overdue ? ` · ${overdue} overdue` : ''}
        </span>
        <div className="topbar-right">
          <button className="btn primary" onClick={() => setCreating(true)}>New task</button>
        </div>
      </div>

      <div className="body" style={{ display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        <div className="row" style={{ gap: 8, marginBottom: 8, flexWrap: 'wrap' }}>
          <button className={`btn ${tab === 'week' ? 'on' : ''}`} onClick={() => setTab('week')}>
            This week <span className="mono" style={{ marginLeft: 4, color: 'var(--tertiary)' }}>{data.week.length}</span>
          </button>
          <button className={`btn ${tab === 'all' ? 'on' : ''}`} onClick={() => setTab('all')}>
            All <span className="mono" style={{ marginLeft: 4, color: 'var(--tertiary)' }}>{data.tasks.length}</span>
          </button>
          <span style={{ width: 10 }} />
          <span className="lbl">Who</span>
          {people.map((p) => (
            <button key={p.email} className={`btn ${who === p.email ? 'on' : ''}`}
              onClick={() => setWho((v) => (v === p.email ? null : p.email))}>
              {p.name}
            </button>
          ))}
        </div>

        <div className="card" style={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
          <div className="tbl-wrap" style={{ flex: 1 }}>
            {tab === 'week' ? (
              week.length === 0 ? (
                <Empty>Nothing open this week.</Empty>
              ) : (
                grouped.map(([email, rows]) => (
                  <div key={email}>
                    <div className="lbl" style={{ padding: '8px 12px 4px', background: 'var(--line-soft)' }}>
                      {nameOf(people, email)} <span className="mono" style={{ color: 'var(--tertiary)' }}>{rows.length}</span>
                    </div>
                    {rows.map((r, i) => (
                      <div key={`${r.task.id}-${r.occurrence?.id || i}`} className="summary-row"
                        onClick={() => setOpenId(r.task.id)}>
                        <div className="row" style={{ gap: 8, alignItems: 'baseline' }}>
                          {r.kind === 'slot' ? (
                            <input type="checkbox" checked={false} onClick={(e) => e.stopPropagation()}
                              onChange={async () => { await api.patchOccurrence(r.occurrence.id, { status: 'done' }); load() }} />
                          ) : null}
                          <span style={{ fontWeight: 500 }}>{r.title}</span>
                          <Chip kind={KIND[r.status]}>{LABEL[r.status]}</Chip>
                          {r.overdue ? <Chip kind="stale">OVERDUE</Chip> : null}
                          {r.task.link?.type === 'account' ? (
                            <button className="btn link" style={{ fontSize: 'var(--fs-pill)' }}
                              onClick={(e) => { e.stopPropagation(); onOpenAccount?.(r.task.link.id) }}>
                              open account
                            </button>
                          ) : null}
                          <span className="mono" style={{ marginLeft: 'auto', fontSize: '10px', color: 'var(--tertiary)' }}>
                            {r.due || 'no date'}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                ))
              )
            ) : allTasks.length === 0 ? (
              <Empty>No tasks yet.</Empty>
            ) : (
              <table className="tbl">
                <thead>
                  <tr>
                    <th>Task</th>
                    <th style={{ width: 110 }}>Assignee</th>
                    <th style={{ width: 100 }}>Start</th>
                    <th style={{ width: 100 }}>Due</th>
                    <th style={{ width: 120 }}>Repeat</th>
                    <th style={{ width: 96 }}>Open</th>
                    <th style={{ width: 110 }}>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {allTasks.map((t) => (
                    <tr key={t.id} onClick={() => setOpenId(t.id)} title="Open the task">
                      <td style={{ fontWeight: 500, whiteSpace: 'normal' }}>
                        {t.title}
                        {t.link ? (
                          <span className="mono" style={{ fontSize: '10px', color: 'var(--tertiary)', marginLeft: 6 }}>
                            {t.link.type}
                          </span>
                        ) : null}
                        {t.attachments?.length ? (
                          <span className="mono" style={{ fontSize: '10px', color: 'var(--tertiary)', marginLeft: 6 }}>
                            {t.attachments.length} file{t.attachments.length === 1 ? '' : 's'}
                          </span>
                        ) : null}
                      </td>
                      <td>{nameOf(people, t.assignee)}</td>
                      <td className="mono">{t.start || '—'}</td>
                      <td className="mono">{t.due || '—'}</td>
                      <td className="mono" style={{ color: t.repeat ? 'var(--ink)' : 'var(--tertiary)' }}>
                        {t.repeat ? `${t.repeat.count}× / ${t.repeat.every}` : 'one-off'}
                      </td>
                      <td className="num">{t.open_count}</td>
                      <td><Chip kind={KIND[t.status]}>{LABEL[t.status]}</Chip></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
          <div className="row" style={{ padding: '8px 12px', borderTop: '0.5px solid var(--line)' }}>
            <span className="card-sub">
              Click a task to open it. Repeating tasks hold one content box and its own attachments per slot.
            </span>
          </div>
        </div>
      </div>

      {creating ? (
        <NewTask people={people} accounts={accounts} campaigns={campaigns}
          onClose={() => setCreating(false)}
          onCreated={(t) => { setCreating(false); load().then(() => setOpenId(t.id)) }} />
      ) : null}

      {open ? (
        <TaskDetail task={open} people={people} accounts={accounts} campaigns={campaigns}
          onClose={() => setOpenId(null)} onChanged={load} />
      ) : null}
    </>
  )
}
